import {ImageUtils} from "@/lib/gooroomee/image-process/ImageUtils";

const ObjectUtils = {
    isClass(func){
        return typeof func === "function" && /^class\s/.test(Function.prototype.toString.call(func));
    },
    getClassStaticMethods(cls) {
        return Object.getOwnPropertyNames(cls)
            .filter(name => typeof cls[name] === "function");
    },
    getInstanceMethods(obj) {
        if(obj.constructor === Object){
            return Object.keys(obj);
        }else{
            let methods = new Set(); // 중복 방지를 위해 Set 사용
            let proto = Object.getPrototypeOf(obj);

            while (proto && proto !== Object.prototype) { // Object.prototype까지 체크하지 않도록 설정
                Object.getOwnPropertyNames(proto).forEach(name => {
                    if (typeof obj[name] === 'function') {
                        methods.add(name);
                    }
                });
                proto = Object.getPrototypeOf(proto); // 부모 클래스로 이동
            }

            const list = [...methods];
            return list.filter(name => typeof obj[name] === "function" && name !== "constructor");
        }
    },
}

const GrmWorkerUtils = {
    isWorkerEnvironment () {
        return typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope;
    },

    isWorkerTransferObject(obj){
        if(obj instanceof ImageBitmap){
            return true;
        }
        let result = [
            'OffscreenCanvas',
            'MediaStreamTrackProcessor', 'MediaStreamTrackGenerator',
            'ReadableStream', 'WritableStream'
        ].find(type => {
            if(!globalThis[type]) return;
            return obj instanceof globalThis[type];
        })
        return !!result;
    },
    convertImageBitmapRequired(image){
        const type = [
            'HTMLImageElement',
            'SVGImageElement',
            'HTMLVideoElement',
            'HTMLCanvasElement',
            'VideoFrame',
            // 'ImageBitmap',
            // 'OffscreenCanvas',
            // 'Blob',
            // 'ImageData',
        ].find(type => {
            if(!globalThis[type]) return;
            if(image instanceof globalThis[type]){
                return true;
            }
        })
        if(type){
            return type;
        }
    },

    setEventHandler (apiObj) {
        if(!this.isWorkerEnvironment()){
            throw 'not worker scope';
        }

        if(!apiObj){
            apiObj = {};
        }
        if(!apiObj.api){
            Object.assign(apiObj, {
                api: async function({api, args}){
                    return apiObj[api].apply(apiObj, args);
                }
            });
        }
        if(!apiObj._exclude_gc){
            // Excluded from gc target.
            Object.assign(apiObj, {
                _exclude_gc: function(){
                    for(let i=0; i<arguments.length; i++){
                        let o = arguments[i];
                        if(!o) continue;
                        o._exclude_gc = true;
                    }
                }
            })
        }


        globalThis.onmessage = async function(event){
            // console.log('# on m->w msg:', event);
            const msg = event.data;
            // postMessage(predictions);

            const apiFunc = apiObj[msg.api];
            if(!apiFunc){
                return;
            }

            const sendResponse = (msg, response) => {
                // if(response === undefined){
                //     return;
                // }
                const reqData = msg.data;
                const resMsg = Object.assign({}, msg);
                delete resMsg.data;
                Object.assign(resMsg, {
                    type: 'res',
                    data: (() => {
                        function parse(obj){
                            const type = typeof obj;
                            if(type === 'function') {
                                return {
                                    __function: true,
                                    srcStr: obj.toString()
                                }
                            }else if(type === 'object' && obj) {
                                for(let key in obj){
                                    let value = obj[key];
                                    obj[key] = parse(value);
                                }
                            }
                            return obj;
                        }

                        const res = parse(response) || {};
                        return res;
                    })()
                })
                try{
                    postMessage(resMsg);
                }finally{
                    function gc(data, parent){
                        if(!data) return;
                        if(Array.isArray(data)){
                            for(let i=0; i<data.length; i++){
                                gc(data[i], data);
                            }
                            return;
                        }
                        const type = typeof data;
                        if(type === 'object'){
                            if(data._exclude_gc === true){
                                return;
                            }
                            for(let key in data){
                                gc(data[key], data);
                            }
                        }
                        ['close', 'dispose'].forEach(method => {
                            if(data[method]){
                                try{
                                    // console.debug('gc :', msg.api, data, parent, method);
                                    data[method]();
                                }catch(e){
                                    console.warn('nsfw worker gc err:', msg.api, data, parent, method, e);
                                }
                            }
                        })
                    }
                    gc(reqData);
                }
            };

            let res;
            try{
                // res = apiFunc(msg.data || {});
                res = apiFunc.call(apiFunc, msg.data || {});
            }catch(e){
                msg.error = e.message;
                sendResponse(msg)
                return;
            }
            if(res instanceof Promise){
                res.then(r => {
                    sendResponse(msg, r)
                }).catch(e => {
                    console.error(msg, e);
                    msg.error = e.message;
                    sendResponse(msg)
                });
            }else{
                sendResponse(msg, res);
            }
        };

        const loadedMsg = {
            type: 'loaded',
            dependencies: {
                ...(() => {
                    return ['OffscreenCanvas', 'VideoFrame'].reduce((data, api) => {
                        data[api] = !!globalThis[api];
                        return data;
                    }, {})
                })()
            }
        }
        global.postMessage(loadedMsg);
    },
};


Object.assign(GrmWorkerUtils, {
    async createWorkerProxy (srcObj, worker, options) {
        let keys;
        if(ObjectUtils.isClass(srcObj)){
            keys = ObjectUtils.getClassStaticMethods(srcObj);
        }else{
            keys = ObjectUtils.getInstanceMethods(srcObj);
        }

        // api
        const API = {
            _msgResponseData: {},

            async send (api, data, workerOptions) {
                if(!this.__id__){
                    this.__id__ = 'grmw-'+Date.now();
                }
                let msgId = this.__id__;
                if(crypto?.randomUUID){
                    msgId += '@' + crypto.randomUUID();
                }else{
                    msgId += '@' + performance.now();
                }
                const msg = {
                    type: 'req',
                    msgId,
                    api,
                    data
                };
                return new Promise((resolve, reject) => {
                    this._msgResponseData[msg.msgId] = {
                        msg,
                        resolve, reject,
                        after: options?.listeners?.after
                    };
                    // console.log('# send msg m->w msg:', msg);
                    worker.postMessage(msg, workerOptions);
                })
            }
        };

        // worker init
        await (async () => {
            return new Promise((resolve, reject) => {
                const onmessage = async e => {
                    const msg = event.data;
                    // console.log('# on w->m msg:', event);

                    if(msg.type === 'res'){
                        const req = API._msgResponseData[msg.msgId];
                        delete API._msgResponseData[msg.msgId];
                        if(!req){
                            return;
                        }
                        if(msg.data && typeof msg.data === 'object'){
                            for(let key in msg.data){
                                let value = msg.data[key];
                                if(typeof value !== 'object') continue;
                                if(!value.__function) continue;
                                try{
                                    eval('msg.data[key] = ' + value.srcStr);
                                }catch(e){
                                    console.error(e);
                                }
                            }
                        }

                        if(msg.error){
                            req.reject(msg.error);
                        }else{
                            let data = msg.data;
                            if(req.after){
                                const result = await req.after(data);
                                if(result){
                                    data = result;
                                }
                            }
                            req.resolve(data);
                        }
                    }
                };

                worker.onmessage = async e => {
                    if(e.data?.type !== 'loaded'){
                        console.log('# worker init msg:', e.data);
                        return;
                    }
                    if(e.data.dependencies){
                        this._workerDependencies = e.data.dependencies;
                    }
                    try{
                        worker.loaded = true;
                        worker.onmessage = onmessage
                        // await API.send('init', this.options);
                        resolve();
                    }catch(e){
                        reject(e);
                    }
                }
            });
        })();

        const addProxyMethod = function(key, proxy){
            proxy = proxy || this;
            proxy[key] = async function(){
                let api = key;
                let args = Array.prototype.map.call(arguments, v=>v);
                const workerOptions = {
                    transfer: []
                };
                if(options?.listeners?.before){
                    const result = await options?.listeners?.before(api, args, workerOptions);
                    if(result){
                        if(Array.isArray(result) && result !== args) {
                            args = result
                        }else{
                            if(result.api){
                                api = result;
                            }
                            if(result.args){
                                args = result.args;
                            }
                        }
                    }
                }

                // trans image data
                await (async () => {
                    async function parse(obj){
                        if(!GrmWorkerUtils.convertImageBitmapRequired(obj)){
                            if(GrmWorkerUtils.isWorkerTransferObject(obj)){
                                if(obj._no_transfer !== true){
                                    workerOptions.transfer.push(obj);
                                }
                                return obj;
                            }
                            if(typeof obj === 'object'){
                                for(let key in obj){
                                    const value = obj[key];
                                    const parseValue = await parse(obj[key]);
                                    if(value !== parseValue){
                                        obj[key] = parseValue;
                                    }
                                }
                                return obj;
                            }
                            return obj;
                        }
                        try{
                            const bitmap = await createImageBitmap(obj);
                            workerOptions.transfer.push(bitmap);
                            return bitmap;
                        }catch(e){
                            console.error(e);
                        }
                    }
                    await parse(args);
                })();

                return API.send('api', {
                    api,
                    args
                }, workerOptions);
            }
            return proxy;
        };

        // create proxy
        const proxy = {
            addProxyMethod
        };
        keys.forEach(key => addProxyMethod(key, proxy));
        proxy._worker_proxy_id = 'grm-worker-proxy@'+performance.now();

        return proxy;
    }
})

export default GrmWorkerUtils;
